"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateCompanySettingsRequest = exports.companySettingsResponse = exports.companyProfile = exports.DEFAULT_COMPANY_SETTINGS = exports.companySettings = exports.periodCheckMode = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
const dashboard_1 = require("./dashboard");
/**
 * إعدادات الشركة — كل قرار محاسبى أو ضريبى للمالك خيار هنا لا ثابت فى الكود
 * (ADR-0006). كل حقل له قيمة افتراضية هى اختيار المالك، فالمخزَّن قد يكون جزئيًا
 * أو قديمًا ويُكمَّل بالافتراضى عند القراءة.
 *
 * تغيير إعداد لا يمس ما رُحّل: المستند المنشور يحتفظ بأثره.
 */
/** فحص قبل إقفال الفترة: يمنع، أو ينبّه فقط، أو لا يُفحص. */
exports.periodCheckMode = zod_1.z.enum(['block', 'warn', 'off']);
exports.companySettings = zod_1.z.object({
    documents: zod_1.z
        .object({
        /** `PREFIX-YYYY-NNNNNN` يتجدد سنويًا، أو مسلسل مستمر بلا سنة. */
        numbering: zod_1.z.enum(['prefix_year_sequence', 'continuous']).default('prefix_year_sequence'),
    })
        .default({}),
    sales: zod_1.z
        .object({
        creditNotes: zod_1.z.boolean().default(true),
        invoiceCancellation: zod_1.z.boolean().default(false),
        debitNotes: zod_1.z.boolean().default(false),
        /**
         * أى حساب يُدان بصافى الإشعار الدائن: مردودات ومسموحات المبيعات (حساب مقابل
         * للإيراد يُبقى الإيراد الأصلى ظاهرًا)، أو حساب إيراد السطر نفسه (عكس مباشر).
         * BLOCKED-ACCOUNTING حتى يعتمد المحاسب أحدهما.
         */
        creditNoteAccount: zod_1.z.enum(['sales_returns', 'original_revenue']).default('sales_returns'),
        /** أين تُسجَّل دفعة العميل قبل الفاتورة: التزام منفصل، أو رصيد دائن على حسابه. */
        customerAdvances: zod_1.z
            .enum(['separate_account', 'customer_balance'])
            .default('separate_account'),
    })
        .default({}),
    purchases: zod_1.z
        .object({
        /** إشعار مدين نصدره للمورد (مرتجع أو خصم) — يخفض ذمته على فاتورة بعينها. */
        debitNotes: zod_1.z.boolean().default(true),
        /** إشعار دائن من المورد بزيادة على فاتورته — مطفأ حتى يحتاجه المالك. */
        creditNotes: zod_1.z.boolean().default(false),
        /** إلغاء فاتورة مورد كاملة بقيد عكسى — مطفأ: التصحيح المعتاد بإشعار مدين. */
        invoiceCancellation: zod_1.z.boolean().default(false),
        /**
         * أى حساب يُقيَّد دائنًا بصافى الإشعار المدين: «مردودات ومسموحات المشتريات» (يُبقى
         * المصروف الأصلى ظاهرًا)، أو حساب سطر الفاتورة نفسه (عكس مباشر). سطور الأصول تُعكس
         * على حسابها دائمًا. BLOCKED-ACCOUNTING حتى يعتمد المحاسب أحدهما.
         */
        debitNoteAccount: zod_1.z
            .enum(['purchase_returns', 'original_account'])
            .default('purchase_returns'),
    })
        .default({}),
    closing: zod_1.z
        .object({
        yearEnd: zod_1.z.enum(['auto_entry', 'manual', 'reports_only']).default('auto_entry'),
        periodChecks: zod_1.z
            .object({
            bankReconciled: exports.periodCheckMode.default('block'),
            openingDifference: exports.periodCheckMode.default('block'),
        })
            .default({}),
    })
        .default({}),
    opening: zod_1.z
        .object({
        difference: zod_1.z.enum(['suspense_account', 'reject', 'equity']).default('suspense_account'),
    })
        .default({}),
    treasury: zod_1.z
        .object({
        /** خزينة أو بنك جديد بلا حساب أستاذ: يُفتح له حساب فرعى تلقائيًا. */
        autoCreateLedgerAccount: zod_1.z.boolean().default(true),
    })
        .default({}),
    inventory: zod_1.z
        .object({
        /** المخزون ميزة اختيارية: شركة خدمات لا تحتاجه، فمطفأ حتى يفعّله المالك. */
        enabled: zod_1.z.boolean().default(false),
        /**
         * طريقة تكلفة المنصرف: المتوسط المرجح (موصى به) أو الوارد أولاً يصرف أولاً.
         * BLOCKED-ACCOUNTING حتى يعتمد المحاسب الطريقة — وتغييرها لا يمس ما رُحّل.
         */
        costMethod: zod_1.z.enum(['weighted_average', 'fifo']).default('weighted_average'),
        /** صرف بلا رصيد كافٍ: مرفوض افتراضيًا، والعجز المسموح يُسعَّر بآخر تكلفة واردة. */
        allowNegativeStock: zod_1.z.boolean().default(false),
    })
        .default({}),
    reports: zod_1.z
        .object({
        /**
         * قائمة التدفقات النقدية بالطريقة غير المباشرة (قرار المالك). تبويب كل حساب آلى من
         * موقعه فى الدليل على ممارسة معيار المحاسبة المصرى 4 / IAS 7، وما يحتمل أكثر من
         * تبويب خيار هنا. الافتراضيات اتُّخذت بالنيابة عن المالك وتُراجع مع المحاسب.
         */
        cashFlow: zod_1.z
            .object({
            /**
             * ما يُعد «نقدية»: كل مجموعة «النقدية وما فى حكمها» و«البنوك والمحافظ» بما فيها
             * العهد والودائع القصيرة (افتراضى — تعريف النقدية وما فى حكمها فى المعيار)، أو
             * الخزائن والبنوك والمحافظ وحدها كحركة النقدية واللوحة.
             */
            cashScope: zod_1.z.enum(['cash_subledgers', 'cash_group']).default('cash_group'),
            /** تبويب حساب بعينه (بكوده) يغلب التبويب الآلى. */
            accountSections: zod_1.z
                .record(zod_1.z.string().regex(/^\d{1,32}$/, 'account code'), zod_1.z.enum(['operating', 'investing', 'financing', 'non_cash']))
                .default({}),
        })
            .default({}),
    })
        .default({}),
    tax: zod_1.z
        .object({
        vatRegistration: zod_1.z.enum(['registered', 'not_registered', 'unknown']).default('unknown'),
        withholding: zod_1.z
            .object({
            /** ما يخصمه العملاء من المدفوع لنا — رصيد لنا لدى المصلحة. */
            onReceipts: zod_1.z.boolean().default(true),
            /**
             * نسب مقترحة تظهر فى شاشة التحصيل للاختيار السريع. `BLOCKED-TAX`: لا نسبة قانونية
             * فى الكود — القائمة فارغة حتى يدخلها المالك بعد مراجعة مستشار الضرائب، وتبقى
             * النسبة قابلة للإدخال يدويًا فى كل إيصال.
             */
            receiptRates: zod_1.z
                .array(zod_1.z.string().regex(/^\d{1,3}(\.\d{1,4})?$/, 'rate is a percent like 1 or 0.5'))
                .max(20)
                .default([]),
            /** ما نخصمه نحن من الموردين ونورّده. */
            onPayments: zod_1.z.boolean().default(false),
            /**
             * نسب مقترحة فى شاشة السداد — `BLOCKED-TAX` كنسب التحصيل: فارغة حتى يدخلها المالك
             * بعد مراجعة مستشار الضرائب، والنسبة تُدخل يدويًا فى كل سداد على أى حال.
             */
            paymentRates: zod_1.z
                .array(zod_1.z.string().regex(/^\d{1,3}(\.\d{1,4})?$/, 'rate is a percent like 1 or 0.5'))
                .max(20)
                .default([]),
        })
            .default({}),
        eInvoice: zod_1.z
            .object({
            /** الإرسال الفعلى للمنظومة — مطفأ حتى حساب المنظومة وبيانات اختبار رسمية. */
            enabled: zod_1.z.boolean().default(false),
        })
            .default({}),
    })
        .default({}),
    /**
     * مركز ترحيل البيانات من برنامج سابق. الدفعة المعتمدة لا تتأثر بتغيير أى إعداد هنا —
     * كل دفعة تحفظ تاريخها وتقريرها كما اعتُمدت.
     */
    migration: zod_1.z
        .object({
        /**
         * تاريخ بداية العمل على البرنامج (cut-over): الأرصدة المرحَّلة أرصدة نهاية اليوم
         * السابق له، وتُقيَّد بهذا التاريخ. null = لم يُحدَّد بعد، والمعالج يقترح أول السنة.
         */
        cutoverDate: zod_1.z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .default(null),
        /**
         * أسماء متقاربة (عميلان «شركة النور» و«شركه النور»): تنبيه يُراجَع (افتراضى)، أو مانع
         * للاعتماد حتى يُدمجا أو يُؤكَّد أنهما مختلفان، أو لا يُفحص.
         */
        similarNames: zod_1.z.enum(['warn', 'block', 'off']).default('warn'),
        /** حد التشابه من ١٠٠ — فوقه يُعد الاسمان متقاربين. */
        similarityPercent: zod_1.z.number().int().min(60).max(99).default(85),
        /**
         * رصيد حساب ضريبى (قيمة مضافة، خصم من المنبع) فى الميزان المرحَّل: يُنقل رصيدًا بلا
         * حدث ضريبى (موصى به — لا يظهر فى إقرار فترة الترحيل لأنه ليس معاملة فيها)، أو
         * يُرفض حتى يُعاد توجيهه. BLOCKED-TAX حتى يعتمده مستشار الضرائب.
         */
        taxBalances: zod_1.z.enum(['carry_without_tax_event', 'reject']).default('carry_without_tax_event'),
        /**
         * موافقة الشركة على إرسال عينات من ملفاتها لمساعد ذكاء اصطناعى يقترح المطابقة
         * (المرحلة ٢). مطفأ افتراضيًا، ولا شىء يُرسل فى المرحلة ١ أيًّا كانت قيمته.
         */
        aiAssist: zod_1.z.boolean().default(false),
    })
        .default({}),
    /**
     * تعريفات اللوحة الرئيسية وحدود تنبيهاتها. لا تمس دفترًا ولا رقمًا منشورًا — تحدد
     * كيف تُقرأ الأرقام وتُقارن، فتغييرها يغيّر العرض وحده.
     */
    dashboard: zod_1.z
        .object({
        /**
         * المقارنة: الفترة السابقة بنفس الطول (الشهر بالشهر السابق، الربع بالربع السابق)،
         * أو نفس الفترة من العام الماضى (تُلغى أثر الموسمية).
         */
        comparison: zod_1.z.enum(['previous_period', 'same_period_last_year']).default('previous_period'),
        /** شهر بداية السنة المالية لـ«منذ بداية السنة» والأرباع: ١ = يناير، ٧ = يوليو. */
        fiscalYearStartMonth: zod_1.z.number().int().min(1).max(12).default(1),
        runway: zod_1.z
            .object({
            /**
             * معدل الصرف: صافى (المدفوع − المحصّل) وهو التعريف المعتاد، أو إجمالى المدفوع
             * وحده (أحوط: كم تكفى النقدية لو توقف التحصيل).
             */
            basis: zod_1.z.enum(['net_burn', 'gross_burn']).default('net_burn'),
            /** متوسط آخر كم شهرًا كاملاً. */
            lookbackMonths: zod_1.z.number().int().min(1).max(12).default(3),
            warningMonths: zod_1.z.number().int().min(1).max(36).default(3),
            criticalMonths: zod_1.z.number().int().min(0).max(36).default(1),
        })
            .default({}),
        insights: zod_1.z
            .object({
            /** نسبة تغير الإيراد عن المقارنة التى تستحق تنبيهًا (صعودًا أو هبوطًا). */
            revenueChangePercent: zod_1.z.number().int().min(1).max(500).default(20),
            /** نسبة زيادة المصروفات التى تستحق تحذيرًا. */
            expenseIncreasePercent: zod_1.z.number().int().min(1).max(500).default(25),
            /** تغير هامش مجمل الربح بالنقاط المئوية. */
            marginChangePoints: zod_1.z.number().int().min(1).max(100).default(5),
            /** العميل «متأخر» حين يتجاوز رصيده هذه الأيام — حدود شرائح الأعمار نفسها. */
            overdueCustomerDays: zod_1.z.union([zod_1.z.literal(30), zod_1.z.literal(60), zod_1.z.literal(90)]).default(90),
            /** الصنف «راكد» حين لا يُباع منه شىء هذه الأيام وله رصيد. */
            slowMovingDays: zod_1.z.number().int().min(7).max(730).default(90),
            /** فاتورة غير محصّلة بهذا المبلغ أو أكثر تُذكر. */
            largeInvoiceAmount: zod_1.z
                .string()
                .regex(/^\d{1,16}(\.\d{1,4})?$/)
                .default('50000'),
            /** فواتير موردين تستحق خلال هذه الأيام. */
            supplierDueDays: zod_1.z.number().int().min(1).max(60).default(7),
            /**
             * يوم استحقاق إقرار القيمة المضافة من الشهر التالى، أو null لآخر يوم فيه
             * («خلال الشهر التالى»). BLOCKED-TAX حتى يعتمده مستشار الضرائب.
             */
            vatReturnDueDay: zod_1.z.number().int().min(1).max(31).nullable().default(null),
            /** قواعد أخفاها المالك من اللوحة. */
            hidden: zod_1.z.array(dashboard_1.insightRule).max(20).default([]),
        })
            .default({}),
    })
        .default({}),
});
/** القيم الافتراضية كاملة — ما يراه من لم يحفظ إعدادًا قط. */
exports.DEFAULT_COMPANY_SETTINGS = exports.companySettings.parse({});
exports.companyProfile = zod_1.z.object({
    legalNameAr: zod_1.z.string().trim().min(1).max(200),
    legalNameEn: zod_1.z.string().trim().max(200).nullable(),
    taxRegistrationNumber: zod_1.z
        .string()
        .trim()
        .regex(/^\d{9}$/, 'tax registration number is 9 digits')
        .nullable(),
    commercialRegistryNumber: zod_1.z.string().trim().max(32).nullable(),
});
exports.companySettingsResponse = zod_1.z.object({
    companyId: common_1.uuid,
    profile: exports.companyProfile,
    functionalCurrency: zod_1.z.string(),
    settings: exports.companySettings,
    updatedAt: zod_1.z.string().nullable(),
});
/** الحفظ يرسل الإعدادات والبيانات كاملة — ما لم يُرسل يعود افتراضيًا، فلا حالة نصف محدثة. */
exports.updateCompanySettingsRequest = zod_1.z
    .object({
    profile: exports.companyProfile,
    settings: exports.companySettings,
})
    .strict();
//# sourceMappingURL=settings.js.map